// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ControlsTypes } from '../../UtilsClasses/FactoryConstants';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { datePickerDropDownHandler } from '@shared/utils/AppHandlers';
import { KeyCode, GetDigit } from '@shared/commons/KeyEventProcessor';
import TerceraDateTimePickerTemplate from '@front/templates/TerceraDateTimePickerTemplate.html';
import { Control } from '../Control';
import { ScrollUtils } from '../../UtilsClasses/ScrollUtils';
import moment from 'moment';
import $ from 'jquery';
import { ContainerControl } from '../ContainerControl';
export class TerceraDateTimePicker extends ContainerControl {
    constructor() {
        super();
        this.dateTimePartGetSetDict = null;
        this.dateTimePartsViewOrder = ['day', 'month', 'year', 'hours', 'minutes', 'seconds'];
        const dateTimePartGetSetDict = {};
        dateTimePartGetSetDict.day = { get: this.getDay.bind(this), set: this.setDay.bind(this) };
        dateTimePartGetSetDict.month = { get: this.getMonth.bind(this), set: this.setMonth.bind(this) };
        dateTimePartGetSetDict.year = { get: this.getYear.bind(this), set: this.setYear.bind(this) };
        dateTimePartGetSetDict.hours = { get: this.getHours.bind(this), set: this.setHours.bind(this) };
        dateTimePartGetSetDict.minutes = { get: this.getMinutes.bind(this), set: this.setMinutes.bind(this) };
        dateTimePartGetSetDict.seconds = { get: this.getSeconds.bind(this), set: this.setSeconds.bind(this) };
        this.dateTimePartGetSetDict = dateTimePartGetSetDict;
        this.DateTimeChanged = new CustomEvent();
    }
    getType() {
        return ControlsTypes.TerceraDateTimePicker;
    }
    oninit() {
        super.oninit();
        this.observe('dateTime', this.onDateTimeChanged);
        this.on('onClick', this.showDateDropDown);
        this.on('containerKeyDown', this.onContainerKeyDown);
        this.on('keyDown', this.onDateTimePartKeyDown);
        this.on('mouseWheel', this.onMouseWheel);
        this.on('containerFocus', this.onContainerFocus);
        this.on('focus', this.onDateTimePartFocus);
        this.on('blur', this.onDateTimePartBlur);
        this.onDateChangedFromDropDown = this.onDateChangedFromDropDown.bind(this);
    }
    showDateDropDown() {
        const $root = $(this.find('div'));
        const $window = $(window);
        const offset = $root.offset();
        const posY = offset.top - $window.scrollTop() + 32;
        const posX = offset.left - $window.scrollLeft();
        void this.set('isDatePickerOpen', true);
        datePickerDropDownHandler.Show(this.get('dateTime'), this.onDateChangedFromDropDown, posX, posY, { maxDate: new Date() }, () => {
            void this.set('isDatePickerOpen', false);
        });
    }
    onDateChangedFromDropDown(date) {
        const curDT = this.get('dateTime');
        curDT.setFullYear(date.getFullYear());
        curDT.setMonth(date.getMonth());
        curDT.setDate(date.getDate());
        void this.set('dateTime', curDT);
    }
    onDateTimeChanged(newVal) {
        this.DateTimeChanged.Raise(this, newVal);
    }
    onContainerFocus() {
        this.setFocus();
    }
    onDateTimePartFocus(event, dateTimePartName) {
        void this.set('focusedDateTimePartName', dateTimePartName);
        this.setFocus();
    }
    onDateTimePartBlur(event, dateTimePartName) {
        void this.set('focusedDateTimePartName', null);
        if (dateTimePartName === 'year') {
            // Validation of keyboard input.
            this.setYear(this.getYear());
        }
    }
    processDateTimePartsNavigation(keyCode) {
        if (keyCode !== KeyCode.LEFT && keyCode !== KeyCode.RIGHT) {
            return false;
        }
        const dateTimePartsViewOrder = this.dateTimePartsViewOrder;
        let resultDateTimePartName = dateTimePartsViewOrder[0];
        const focusedDateTimePartName = this.get('focusedDateTimePartName');
        if (focusedDateTimePartName) {
            let idx = dateTimePartsViewOrder.indexOf(focusedDateTimePartName);
            idx = idx + (keyCode === KeyCode.LEFT ? -1 : 1);
            const maxIdx = dateTimePartsViewOrder.length - 1;
            if (idx > maxIdx) {
                idx = 0;
            }
            else if (idx < 0) {
                idx = maxIdx;
            }
            resultDateTimePartName = dateTimePartsViewOrder[idx];
        }
        const div = this.find('.' + resultDateTimePartName);
        div.focus();
        return true;
    }
    onContainerKeyDown(event) {
        this.processDateTimePartsNavigation(event.original.which);
        return false;
    }
    onDateTimePartKeyDown(event, dateTimePartName) {
        const keyCode = event.original.which;
        if (this.processDateTimePartsNavigation(keyCode)) {
            return false;
        }
        if (keyCode === KeyCode.UP || keyCode === KeyCode.DOWN) {
            this.setScrollValue(dateTimePartName, keyCode === KeyCode.UP);
            return false;
        }
        const digit = GetDigit(keyCode);
        if (isNaN(digit))
            return false;
        const dtPartGetSetObj = this.dateTimePartGetSetDict[dateTimePartName];
        const curDTPartValue = dtPartGetSetObj.get.call(this);
        const newDTPartValue = parseInt(curDTPartValue.toString() + digit.toString());
        // TODO.
        const options = {
            setOnlyIfValidValue: true,
            valueFromKeyboardInput: true
        };
        const dtPartSetFunc = dtPartGetSetObj.set;
        const valueWithinBounds = dtPartSetFunc.call(this, newDTPartValue, options);
        if (!valueWithinBounds) {
            dtPartSetFunc.call(this, digit, options);
        }
        return false;
    }
    onMouseWheel(context, dateTimePartName) {
        const event = context.event;
        if (this.get('focusedDateTimePartName') !== dateTimePartName) {
            return false;
        }
        this.setScrollValue(dateTimePartName, ScrollUtils.IsScrollUp(event.deltaY));
        return false;
    }
    setScrollValue(dateTimePartName, setNextValue) {
        const getSetObj = this.dateTimePartGetSetDict[dateTimePartName];
        let val = getSetObj.get();
        val += setNextValue ? 1 : -1;
        getSetObj.set(val);
    }
    // #region dateTime GET/SET Methods
    getDay() {
        return this.get('dateTime').getDate();
    }
    setDay(value, options) {
        const dt = this.get('dateTime');
        const daysInMonth = moment(dt).endOf('month').date();
        let valueWithinLimits = true;
        if (value < 1) {
            value = daysInMonth;
            valueWithinLimits = false;
        }
        else if (value > daysInMonth) {
            value = 1;
            valueWithinLimits = false;
        }
        if ((options === null || options === void 0 ? void 0 : options.setOnlyIfValidValue) && !valueWithinLimits) {
            return valueWithinLimits;
        }
        dt.setDate(value);
        void this.update('dateTime');
        return valueWithinLimits;
    }
    getMonth() {
        return this.get('dateTime').getMonth() + 1;
    }
    setMonth(value, options) {
        let valueWithinLimits = true;
        if (value < 1) {
            value = 12;
            valueWithinLimits = false;
        }
        else if (value > 12) {
            value = 1;
            valueWithinLimits = false;
        }
        if ((options === null || options === void 0 ? void 0 : options.setOnlyIfValidValue) && !valueWithinLimits) {
            return valueWithinLimits;
        }
        this.get('dateTime').setMonth(value - 1);
        void this.update('dateTime');
        return valueWithinLimits;
    }
    getYear() {
        return this.get('dateTime').getFullYear();
    }
    setYear(value, options) {
        let minYear;
        let maxYear;
        const valueFromKeyboardInput = options === null || options === void 0 ? void 0 : options.valueFromKeyboardInput;
        if (valueFromKeyboardInput) {
            minYear = 1;
            maxYear = 9999;
        }
        else {
            minYear = 1900;
            maxYear = 2099;
        }
        let valueWithinLimits = true;
        if (value < minYear) {
            value = maxYear;
            valueWithinLimits = false;
        }
        else if (value > maxYear) {
            value = minYear;
            valueWithinLimits = false;
        }
        if ((options === null || options === void 0 ? void 0 : options.setOnlyIfValidValue) && !valueWithinLimits) {
            return valueWithinLimits;
        }
        this.get('dateTime').setFullYear(value);
        void this.update('dateTime');
        return valueWithinLimits;
    }
    getHours() {
        return this.get('dateTime').getHours();
    }
    setHours(value, options) {
        let valueWithinLimits = true;
        if (value < 0) {
            value = 23;
            valueWithinLimits = false;
        }
        else if (value > 23) {
            value = 0;
            valueWithinLimits = false;
        }
        if ((options === null || options === void 0 ? void 0 : options.setOnlyIfValidValue) && !valueWithinLimits) {
            return valueWithinLimits;
        }
        this.get('dateTime').setHours(value);
        void this.update('dateTime');
        return valueWithinLimits;
    }
    getMinutes() {
        const t = this.get('dateTime');
        return this.get('dateTime').getMinutes();
    }
    setMinutes(value, options) {
        let valueWithinLimits = true;
        if (value < 0) {
            value = 59;
            valueWithinLimits = false;
        }
        else if (value > 59) {
            value = 0;
            valueWithinLimits = false;
        }
        if ((options === null || options === void 0 ? void 0 : options.setOnlyIfValidValue) && !valueWithinLimits) {
            return valueWithinLimits;
        }
        this.get('dateTime').setMinutes(value);
        void this.update('dateTime');
        return valueWithinLimits;
    }
    getSeconds() {
        return this.get('dateTime').getSeconds();
    }
    setSeconds(value, options) {
        let valueWithinLimits = true;
        if (value < 0) {
            value = 59;
            valueWithinLimits = false;
        }
        else if (value > 59) {
            value = 0;
            valueWithinLimits = false;
        }
        if ((options === null || options === void 0 ? void 0 : options.setOnlyIfValidValue) && !valueWithinLimits) {
            return valueWithinLimits;
        }
        this.get('dateTime').setSeconds(value);
        void this.update('dateTime');
        return valueWithinLimits;
    }
}
Control.extendWith(TerceraDateTimePicker, {
    template: TerceraDateTimePickerTemplate,
    data: () => ({
        dateTime: null,
        dateFromStyle: false,
        focusedDateTimePartName: null,
        isDatePickerOpen: false
    }),
    computed: {
        // For view.
        day() {
            return moment(this.get('dateTime')).format('DD');
        },
        month() {
            return moment(this.get('dateTime')).format('MM');
        },
        year() {
            return moment(this.get('dateTime')).format('YYYY');
        },
        hours() {
            return moment(this.get('dateTime')).format('HH');
        },
        minutes() {
            return moment(this.get('dateTime')).format('mm');
        },
        seconds() {
            return moment(this.get('dateTime')).format('ss');
        }
    }
});
